import { useEffect } from 'react';
import { AfterPaymentActionEnum } from '../enums/after-payment';
import useCheckoutState, { checkoutStates } from './useCheckoutState';
import useRedirectAfterPayment from './useRedirectAfterPayment';
import useCheckoutUpsell from './useCheckoutUpsell';
import getUpsellFunnelStep from '../lib/getUpsellFunnelStep';
import getCheckoutSlug from '../services/getCheckoutSlug';

const useAfterPaymentActions = () => {
  const { checkout, isUpsell, state, setState, query, resetForm } = useCheckoutState();
  const { afterPaymentAction } = checkout;
  const { redirectConfirmation, redirectCheckout, redirectURL } = useRedirectAfterPayment();
  const { handleUpsellAction, handleAfterUpsellAction } = useCheckoutUpsell();

  useEffect(() => {
    // Only reset the form when we're actually redirecting away
    const shouldCleanup = state === checkoutStates.COMPLETED;

    return () => {
      if (shouldCleanup) {
        // add delay to ensure we've handled the after payment actions before resetting the state
        setTimeout(() => {
          setState(checkoutStates.INITIAL);
          resetForm();
        }, 3000);
      }
    };
  }, [state]);

  const upsellFunnelStep = getUpsellFunnelStep(checkout);

  async function handleAfterPaymentActions(payment) {
    if (isUpsell) {
      return handleAfterUpsellAction();
    }

    if (upsellFunnelStep && !isUpsell) {
      return handleUpsellAction(upsellFunnelStep, payment, checkout);
    }

    switch (afterPaymentAction) {
      case 'confirmation':
        redirectConfirmation(payment._id, getCheckoutSlug(checkout));
        break;
      case 'checkout':
        redirectCheckout(payment, getCheckoutSlug(checkout.redirectCheckout));
        break;
      case 'redirect': {
        const dynamicRedirectUrl =
          query.redirect_url && checkout.allowDynamicRedirectUrl ? query.redirect_url : null;

        const redirectUrl = dynamicRedirectUrl || checkout.redirectUrl;

        redirectURL(
          payment,
          {
            url: redirectUrl,
            path: checkout.redirectUrlPath,
            query: checkout.redirectUrlQuery,
          },
          checkout.redirectUrlInsideEmbed,
        );
        break;
      }
      default:
        break;
    }
  }

  const isConfirmationAfterPayment = afterPaymentAction === AfterPaymentActionEnum.CONFIRMATION;
  const isUpsellAfterPayment = !!upsellFunnelStep;
  const isCheckoutUpsell = isUpsell;

  // if any of the upsell conditions is met, don't show confirmation
  const isUpsellConditionMet = isUpsellAfterPayment || isCheckoutUpsell;

  const shouldShowConfirmation = isConfirmationAfterPayment && !isUpsellConditionMet;

  return {
    handleAfterPaymentActions,
    shouldShowConfirmation,
  };
};

export default useAfterPaymentActions;
